// @ts-nocheck
/* eslint-disable */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import useAppStore, { useCalculationsSelector } from '../store/appStore';
import { usePersonalDataSelector } from '../store/usePersonalDataSelector';
import UI from '../components/UI';
import { TrendingDown, TrendingUp, Minus, ShieldAlert, UploadCloud, Trash2, ChevronDown, CheckCircle, AlertTriangle, XCircle, Pencil, Lock, Unlock } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { evaluateRentabilitaet } from '../store/utils'; // 💉 CHIRURGIE: Doppel-Schloss Import

// ==========================================
// FORMATTER & PARSER HELPER (JetBrains Mono)
// ==========================================
const parse = (val: any): number => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    const num = parseFloat(String(val).replace(/\./g, '').replace(',', '.'));
    return isNaN(num) ? 0 : num;
};

const formatValue = (val: number, decimals: number = 0) => new Intl.NumberFormat('de-DE', { 
    style: 'decimal', 
    maximumFractionDigits: decimals, 
    minimumFractionDigits: decimals 
}).format(val || 0);

// ==========================================
// CUSTOM STYLES / DESIGN UTILS
// ==========================================
const CustomStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }

        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }

        @keyframes blink { 50% { border-color: #ef4444; box-shadow: 0 0 15px rgba(239, 68, 68, 0.6); } }
        .blinking-border { animation: blink 1.5s linear infinite; border: 2px solid transparent !important; }
        
        .unit-text { color: #64748b; font-weight: 800; font-size: 0.7rem; letter-spacing: 0.05em; text-transform: uppercase; font-family: 'Inter', sans-serif;}

        /* Carbon Weave Design */
        .carbon-weave-bg {
            background-color: #111;
            background-image:
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        .carbon-panel {
            position: relative;
            border-radius: 1.5rem;
            border: 1px solid #1a1a1a;
            box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.02);
            overflow: hidden;
            z-index: 1;
        }

        .carbon-panel::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.55));
            pointer-events: none;
            z-index: 1;
        }

        .carbon-content {
            position: relative;
            z-index: 10;
        }

        /* TOOLTIP STYLES */
        [data-tooltip-text] { position: relative; }
        [data-tooltip-text]:hover::after {
            content: attr(data-tooltip-text);
            position: absolute;
            bottom: 100%; left: 50%; transform: translateX(-50%);
            margin-bottom: 12px;
            background-color: #E6C299; color: #000000; border: 1px solid #000000;
            font-weight: 800; font-family: 'Inter', sans-serif;
            padding: 10px 14px; border-radius: 8px;
            white-space: normal; z-index: 1000; font-size: 0.75rem;
            box-shadow: 0 10px 25px rgba(0,0,0,0.8); width: max-content;
            max-width: 250px; text-align: center; pointer-events: none;
        }
        [data-tooltip-text]:hover::before {
            content: '';
            position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
            margin-bottom: 2px; border-width: 6px; border-style: solid;
            border-color: #E6C299 transparent transparent transparent;
            z-index: 1000;
        }

        /* ===== LIGHT-MODE OVERRIDES (Warm-Platin) — Dark bleibt via html.dark unangetastet ===== */
        html:not(.dark) .carbon-weave-bg {
            background-color: var(--pe-bg-elevated);
            background-image: none;
        }
        html:not(.dark) .carbon-panel {
            border-color: var(--pe-border-subtle);
            box-shadow: 0 20px 45px -20px rgba(28, 25, 23, 0.18), 0 2px 8px rgba(28, 25, 23, 0.06);
        }
        html:not(.dark) .carbon-panel::after { background: none; }
    `}</style>
);

// ==========================================
// SUB-KOMPONENTEN (Carbon Panels)
// ==========================================
const Section = ({ children, className = '' }: any) => <section className={`border border-pe-border dark:border-[#222] rounded-2xl overflow-hidden ${className}`}>{children}</section>;
const SectionTitle = ({ children, className = '' }: any) => <h2 className={`text-lg font-bold p-4 bg-pe-sunken text-pe-accent-text border-b border-pe-border dark:border-[#222] uppercase tracking-widest ${className}`}>{children}</h2>;

const StatusIndicator = ({ percentage }: { percentage: number }) => {
    if (percentage >= 10) {
        return <CheckCircle className="h-6 w-6 text-green-500 drop-shadow-md" strokeWidth={2.5} />;
    } else if (percentage >= 0) {
        return <AlertTriangle className="h-6 w-6 text-yellow-500 drop-shadow-md" strokeWidth={2.5} />;
    } else {
        return <XCircle className="h-6 w-6 text-red-500 drop-shadow-md" strokeWidth={2.5} />;
    }
};

const ValueCard = ({ label, value, icon, color, isPercentage = false, decimalPlaces = 0, currencyCode = 'EUR' }: any) => (
    <div className={`bg-white dark:bg-pe-tile-header p-3 rounded-lg shadow-md flex items-center border-l-4 ${color} border border-stone-300 dark:border-[#222] text-left`}>
        <div className="mr-3 text-stone-700 dark:text-pe-text">{icon}</div>
        <div className="flex-1 text-left">
            <div className="text-stone-600 dark:text-gray-400 uppercase font-bold text-[10px] tracking-wide">{label}</div>
            <div className="font-black text-stone-900 dark:text-pe-text text-lg flex items-baseline gap-1 tabular-nums">
                {isPercentage ? `${formatValue(value, 1)}%` : <>{formatValue(value, decimalPlaces)} <span className="text-sm text-stone-500 dark:text-gray-400 font-medium ml-1">{currencyCode}</span></>}
            </div>
        </div>
    </div>
);

// ==========================================
// DIFFERENZ-ANZEIGE KOMPONENTE (AMPEL)
// ==========================================
const DiffDisplay = ({ current, prev, isRevenue = false, hasVorjahrData = true, currencyCode = 'EUR', align = 'right' }: { current: number; prev: number; isRevenue?: boolean, hasVorjahrData?: boolean, currencyCode?: string, align?: 'left'|'right'|'center' }) => {
    
    if (!hasVorjahrData || current === 0) {
        return <div className={`text-stone-400 dark:text-gray-600 font-mono font-bold w-full text-${align}`}>-</div>;
    }

    const diff = current - prev;
    let isGood = isRevenue ? diff > 0 : diff < 0;
    if (diff === 0) isGood = true;

    const colorClass = diff === 0 ? 'text-stone-600 dark:text-gray-500' : isGood ? 'text-emerald-800 dark:text-green-500' : 'text-red-800 dark:text-red-500';
    const Icon = diff === 0 ? Minus : diff > 0 ? TrendingUp : TrendingDown;
    const prefix = diff > 0 ? '+' : '';
    const justifyClass = align === 'left' ? 'justify-start' : align === 'center' ? 'justify-center' : 'justify-end';

    return (
        <div className={`flex items-center ${justifyClass} gap-1.5 ${colorClass} font-mono font-bold`}>
            <span>{prefix}{formatValue(diff)} <span className="text-[0.65em] opacity-80 uppercase tracking-widest">{currencyCode}</span></span>
            {diff !== 0 && <Icon className="w-[1em] h-[1em]" />}
        </div>
    );
};

// ==========================================
// KOMPLETTE KURZAUSWERTUNG (BLOCK-KOMPONENTE)
// ==========================================
// 💉 CHIRURGIE: gehalt Property hinzugefügt für das Doppel-Schloss
const KurzauswertungBlock = ({ calc, title, isMock = false, hasData = true, currencyCode = 'EUR', gehalt = 0 }: { calc: any, title: string, isMock?: boolean, hasData?: boolean, currencyCode?: string, gehalt?: number }) => {
    
    // 💉 CHIRURGIE: Das Doppel-Schloss übernimmt die Kontrolle
    const rentabilityStatus = useMemo(() => {
        if (!calc.rentabilitaetSoll && !calc.ueberschussProzent) {
            return { color: 'text-stone-500 dark:text-gray-500', bgColor: 'bg-stone-200/50 dark:bg-gray-900/50', borderColor: 'border-stone-300 dark:border-gray-700/40', label: 'KEINE DATEN' };
        }
        
        const evalResult = evaluateRentabilitaet(gehalt, calc.ueberschussProzent || 0);

        // Light: weiße Karte + dunkler Text/Rand (kein Gelb-Flächenlook). Dark unverändert.
        switch (evalResult.status) {
            case 'Exzellent': return { color: 'text-purple-950 dark:text-purple-400', bgColor: 'bg-white dark:bg-black/40', borderColor: 'border-purple-600 dark:border-purple-500', label: 'EXZELLENT' };
            case 'Gut': return { color: 'text-emerald-950 dark:text-emerald-500', bgColor: 'bg-white dark:bg-emerald-950/20', borderColor: 'border-emerald-700 dark:border-emerald-600', label: 'GUT' };
            case 'Solide': return { color: 'text-lime-950 dark:text-lime-500', bgColor: 'bg-white dark:bg-lime-950/20', borderColor: 'border-lime-700 dark:border-lime-500', label: 'SOLIDE' };
            case 'Grenzwertig': return { color: 'text-[#5C3D18] dark:text-amber-400', bgColor: 'bg-white dark:bg-amber-950/20', borderColor: 'border-[#8D6435] dark:border-amber-500', label: 'GRENZWERTIG' };
            case 'Kritisch':
            default: return { color: 'text-red-950 dark:text-red-500', bgColor: 'bg-white dark:bg-red-950/30', borderColor: 'border-red-600 dark:border-red-600', label: 'KRITISCH BIS INSTABIL' };
        }
    }, [calc.rentabilitaetSoll, calc.ueberschussProzent, gehalt]);

    const status = useMemo(() => {
        const ueberschussProzent = calc.ueberschussProzent || 0;
        const target = 10;
        if (ueberschussProzent === 0 && calc.sollumsatz === 0) return { text: 'Keine Daten', color: 'text-stone-600 dark:text-gray-500', bgColor: 'bg-stone-100 dark:bg-gray-900/50', borderColor: 'border-stone-400 dark:border-gray-700' };
        if (ueberschussProzent >= target) return { text: 'Ziel erreicht', color: 'text-emerald-950 dark:text-green-400', bgColor: 'bg-white dark:bg-green-900/50', borderColor: 'border-emerald-700 dark:border-green-500' };
        if (ueberschussProzent > 0) return { text: 'Kostendeckend', color: 'text-[#5C3D18] dark:text-amber-400', bgColor: 'bg-white dark:bg-amber-950/20', borderColor: 'border-[#8D6435] dark:border-amber-500' };
        return { text: 'Verlust', color: 'text-red-950 dark:text-red-400', bgColor: 'bg-white dark:bg-red-950/50', borderColor: 'border-red-600 dark:border-red-500' };
    }, [calc.ueberschussProzent, calc.sollumsatz]);

    if (!hasData) {
        return (
            <div className={`p-6 rounded-xl border flex flex-col items-center justify-center min-h-[300px] carbon-weave-bg border-pe-border dark:border-[#222] ${isMock ? 'opacity-90' : ''}`}>
                <h3 className={`${isMock ? 'text-stone-500 dark:text-gray-500' : 'text-pe-accent-text'} font-bold uppercase tracking-widest mb-2`}>{title}</h3>
                <span className="text-stone-400 dark:text-gray-600 font-mono text-sm tracking-widest uppercase mt-4">Keine historischen Daten vorhanden</span>
            </div>
        );
    }

    return (
        <div className={`p-6 rounded-xl border carbon-weave-bg border-pe-border dark:border-[#222] ${isMock ? 'opacity-90' : ''}`}>
            <h3 className={`${isMock ? 'text-stone-500 dark:text-gray-500 border-pe-border dark:border-[#222]' : 'text-pe-accent-text border-pe-border dark:border-[#222]'} font-bold uppercase tracking-widest mb-6 border-b pb-2`}>
                {title}
            </h3>
            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <ValueCard label="Gemeinkosten" value={(calc.gemeinkostenfaktor || 0) * 100} isPercentage={true} color="border-cyan-500" icon={<TrendingUp className="w-5 h-5"/>} currencyCode={currencyCode} />
                    <ValueCard label="Material" value={(calc.materialkostenfaktor || 0) * 100} isPercentage={true} color="border-teal-500" icon={<TrendingUp className="w-5 h-5"/>} currencyCode={currencyCode} />
                    <ValueCard label="Geräte" value={(calc.geraetekostenfaktor || 0) * 100} isPercentage={true} color="border-slate-500" icon={<TrendingUp className="w-5 h-5"/>} currencyCode={currencyCode} />
                    <ValueCard label="Lohn (prod.)" value={(calc.prodLohnkostenfaktor || 0) * 100} isPercentage={true} color="border-indigo-500" icon={<TrendingUp className="w-5 h-5"/>} currencyCode={currencyCode} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <ValueCard label="Gesamtumsatz (netto)" value={calc.monatsumsatzNetto} color="border-green-500" icon={<TrendingUp className="w-5 h-5"/>} currencyCode={currencyCode} />
                    <ValueCard label="Gesamtkosten (Sollumsatz)" value={calc.sollumsatz} color="border-red-500" icon={<TrendingDown className="w-5 h-5"/>} currencyCode={currencyCode} />
                </div>
                <div className={`rounded-lg p-4 shadow-md flex items-center border-2 ${status.borderColor} ${status.bgColor} w-full text-left mt-2`}>
                    <div className="flex-1 text-left">
                        <div className="text-xs text-stone-600 dark:text-slate-400 uppercase font-bold tracking-wider">kalk. Überschuss / Verlust</div>
                        <div className="flex items-baseline space-x-3 mt-1 font-mono">
                            <span className={`text-2xl font-black ${status.color}`}>{formatValue(calc.ueberschuss)}</span>
                            <span className="text-sm text-stone-500 dark:text-gray-400 font-semibold uppercase">{currencyCode}</span>
                            <span className={`text-lg font-black ${status.color}`}>({formatValue(calc.ueberschussProzent || 0, 1)}%)</span>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] text-stone-600 dark:text-slate-400 uppercase font-semibold">Ziel: mind. 10%</div>
                        <div className={`font-black text-sm mt-1 uppercase tracking-wide ${status.color}`}>{status.text}</div>
                    </div>
                </div>
                <div className={`rounded-lg p-3 shadow-md flex items-center border-2 ${rentabilityStatus.borderColor} ${rentabilityStatus.bgColor} w-full`}>
                    <div className="flex-1 text-left">
                        <div className="text-[10px] text-stone-600 dark:text-slate-400 uppercase font-bold tracking-widest">Kalkulatorische Rentabilität</div>
                        <div className="flex items-baseline space-x-2 mt-0.5 font-mono">
                            <span className={`text-xl font-black ${rentabilityStatus.color}`}>{formatValue(calc.rentabilitaetSoll, 1)}%</span>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[9px] text-stone-600 dark:text-slate-500 uppercase font-semibold">Status</div>
                        <div className={`font-black text-xs uppercase tracking-wide ${rentabilityStatus.color}`}>{rentabilityStatus.label}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// HAUPTKOMPONENTE
// ==========================================
const VorjahresKostenPage = () => {
    const calc = useCalculationsSelector();
    const { personalData, unproductivePersonalData, azubiAssistenzData } = usePersonalDataSelector();
    
    const { clientData, setClientData, settings } = useAppStore(state => ({
        clientData: state.clientData,
        setClientData: state.setClientData,
        settings: state.settings,
    }));

    const [isGodMode, setIsGodMode] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsGodMode(!!sessionStorage.getItem('GOD_MODE_ID'));
        }
    }, []);
    
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importError, setImportError] = useState<string | null>(null);
    const [importSuccess, setImportSuccess] = useState(false);

    const currencyCode = settings.country?.currency || 'EUR';
    const effectiveMwst =
        calc?.mwstSatz !== undefined && calc?.mwstSatz !== null && Number.isFinite(Number(calc.mwstSatz))
            ? Number(calc.mwstSatz)
            : clientData.isVatExempt
                ? 0
                : (settings.country?.MwSt_Satz ?? 19);
    const mwstFaktor = 1 + effectiveMwst / 100;

    const unternehmerRawData = useMemo(() => clientData?.unternehmerData || {}, [clientData?.unternehmerData]);
    const d = useMemo(() => calc.bwaData || {}, [calc.bwaData]);
    const zeitraum = calc.zeitraum || 12;
    const currentVisibleCount = d.visibleGeraeteCount ?? 4;

    const vBwa = useMemo(() => clientData?.vorjahresBwaData || {}, [clientData?.vorjahresBwaData]);
    const vUnternehmer = useMemo(() => clientData?.vorjahresUnternehmerData || {}, [clientData?.vorjahresUnternehmerData]);
    const vPersonal = useMemo(() => clientData?.vorjahresPersonalData || [], [clientData?.vorjahresPersonalData]);
    
    const vCalcSnapshot = useMemo(() => clientData?.vorjahresCalcData || clientData?.vorjahresBwaData?.calcSnapshot || null, [clientData?.vorjahresCalcData, clientData?.vorjahresBwaData]);

    const hasVorjahrData = useMemo(() => {
        const isExplicitlyImported = !!clientData?.vorjahresBwaData?.isImported;
        const hasMultipleYears = (clientData?.geschaeftsjahre?.length || 0) > 1;
        const hasBwaData = Object.keys(vBwa).length > 0;
        return isExplicitlyImported || hasMultipleYears || hasBwaData;
    }, [clientData?.vorjahresBwaData?.isImported, clientData?.geschaeftsjahre, vBwa]);

    // 1. BWA Datenaufbereitung & Gruppierung
    const { materialDaten, betriebskostenDaten, geraeteDaten, preisExpertDaten, positiveDaten } = useMemo(() => {
        const filterEmptyRows = (rows: any[]) => rows.filter(r => r.istMonat !== 0 || r.vorjahrMonat !== 0);

        const getPriorYearValue = (key: string) => {
            if (!hasVorjahrData) return 0;
            return vBwa[key] !== undefined ? (parse(vBwa[key]) / zeitraum) : 0; 
        };

        // Material wie Kostenstruktur: skaliert auf Umsatz (materialkostenMonat), sonst BWA÷Zeitraum
        const matRaw = parse(d.material) / zeitraum;
        const matVal = Number(calc.materialkostenMonat) > 0 ? Number(calc.materialkostenMonat) : matRaw;
        const matVorjahrRaw = getPriorYearValue('material');
        const matVorjahrScaled = hasVorjahrData && vCalcSnapshot ? (Number(vCalcSnapshot.materialkostenMonat) || 0) : 0;
        const matRows = [{
            label: 'Materialeinsatz',
            isRevenue: false,
            istMonat: matVal,
            vorjahrMonat: matVorjahrScaled > 0 ? matVorjahrScaled : matVorjahrRaw,
        }];
        
        const betrRows: any[] = [];
        const betriebskostenKeys = [
            { key: 'raumkosten', label: 'Raumkosten' }, { key: 'betriebssteuern', label: 'Betriebliche Steuern' },
            { key: 'versicherungen', label: 'Versicherungen/Beiträge' }, { key: 'besondereKosten', label: 'besondere Kosten' },
            { key: 'kfzKosten', label: 'KFZ Kosten' }, { key: 'werbeReisekosten', label: 'Werbe/Reisekosten' },
            { key: 'warenabgabe', label: 'Warenabgabe an MA' }, { key: 'reparatur', label: 'Reparatur/Instandhaltung' },
            { key: 'sonstigeKosten', label: 'sonstige Kosten' }, { key: 'kredite', label: 'Kredite, Zinsen u.ä.' },
            { key: 'zinsaufwendungen', label: 'Zinsaufwendungen' }, { key: 'gewerbesteuer', label: 'Gewerbesteuer' },
            { key: 'koerperschaftssteuer', label: 'Körperschaftssteuer (GmbH)' }
        ];
        for (let i = 1; i <= 4; i++) {
            const label = d[`sonstigeEintrag${i}_label`];
            if (label) betriebskostenKeys.push({ key: `sonstigeEintrag${i}`, label: label });
        }
        betriebskostenKeys.forEach(field => {
            const val = parse(d[field.key]) / zeitraum;
            betrRows.push({ label: field.label, isRevenue: false, istMonat: val, vorjahrMonat: getPriorYearValue(field.key) });
        });

        const pxVal = parse(d.preisExpert) / zeitraum;
        const pxRows = [{ label: 'PreisExpert Beratung', isRevenue: false, istMonat: pxVal, vorjahrMonat: getPriorYearValue('preisExpert') }];

        const geraeteRows: any[] = [];
        for (let i = 1; i <= currentVisibleCount; i++) {
            const key = `geraetEintrag${i}`;
            const label = d[`${key}_label`] || `Gerät ${i}`;
            const val = parse(d[key]) / zeitraum;
            if (val > 0 || d[`${key}_label`]) {
                geraeteRows.push({ label: label, isRevenue: false, istMonat: val, vorjahrMonat: getPriorYearValue(key) });
            }
        }

        const positiveRows: any[] = [];
        const positiveKeys = [{ key: 'abschreibungen', label: 'Abschreibungen' }, { key: 'zinsenMiete', label: 'Sonstige Einnahmen' }];
        positiveKeys.forEach(field => {
            const val = parse(d[field.key]) / zeitraum;
            positiveRows.push({ label: field.label, isRevenue: true, istMonat: val, vorjahrMonat: getPriorYearValue(field.key) });
        });

        return { 
            materialDaten: filterEmptyRows(matRows), 
            betriebskostenDaten: filterEmptyRows(betrRows), 
            geraeteDaten: filterEmptyRows(geraeteRows),
            preisExpertDaten: filterEmptyRows(pxRows),
            positiveDaten: filterEmptyRows(positiveRows) 
        };
    }, [d, zeitraum, currentVisibleCount, vBwa, hasVorjahrData, calc.materialkostenMonat, vCalcSnapshot]);

    const allBwaRows = useMemo(() => [...materialDaten, ...betriebskostenDaten, ...geraeteDaten, ...preisExpertDaten, ...positiveDaten], [materialDaten, betriebskostenDaten, geraeteDaten, preisExpertDaten, positiveDaten]);
    const summeIstMonat = useMemo(() => allBwaRows.reduce((acc, val) => acc + (!val.isRevenue ? val.istMonat : 0), 0), [allBwaRows]);
    const summeVorjahrMonat = useMemo(() => allBwaRows.reduce((acc, val) => acc + (!val.isRevenue ? val.vorjahrMonat : 0), 0), [allBwaRows]);
    const summeIstJahr = useMemo(() => summeIstMonat * zeitraum, [summeIstMonat, zeitraum]);
    const summeVorjahrJahr = useMemo(() => summeVorjahrMonat * zeitraum, [summeVorjahrMonat, zeitraum]);

    // 2. Unternehmer Daten
    const unternehmerDaten = useMemo(() => {
        const entnahmeIst = parse(unternehmerRawData.entnahme);
        const sozialIst = parse(unternehmerRawData.sozialkosten);
        const ekSteuerIst = parse(calc.erwarteteEinkommenssteuer);
        
        const isImported = !!vBwa?.isImported;
        let entnahmeVorjahr = hasVorjahrData ? parse(vUnternehmer.entnahme) : 0;
        let sozialVorjahr = hasVorjahrData ? parse(vUnternehmer.sozialkosten) : 0;
        let steuerSatzOderWertVorjahr = hasVorjahrData ? parse(vUnternehmer.einkommenssteuer) : 0;

        if (isImported) {
            if (entnahmeVorjahr > 10000) entnahmeVorjahr = entnahmeVorjahr / zeitraum;
            if (sozialVorjahr > 5000) sozialVorjahr = sozialVorjahr / zeitraum;
        }

        let ekSteuerVorjahr = 0;
        if (steuerSatzOderWertVorjahr > 100) {
            ekSteuerVorjahr = (isImported && steuerSatzOderWertVorjahr > 5000) ? (steuerSatzOderWertVorjahr / zeitraum) : steuerSatzOderWertVorjahr;
        } else {
            ekSteuerVorjahr = (entnahmeVorjahr + sozialVorjahr) * (steuerSatzOderWertVorjahr / 100);
        }

        return {
            entnahme: { label: 'Privatentnahme / Geschäftsführergehalt', istMonat: entnahmeIst, vorjahrMonat: entnahmeVorjahr },
            sozial: { label: 'Sozialkosten (KV, RV, Pflege, etc.)', istMonat: sozialIst, vorjahrMonat: sozialVorjahr },
            ekSteuer: { label: 'Zu erwartende Steuer (Monat)', istMonat: ekSteuerIst, vorjahrMonat: ekSteuerVorjahr },
            gesamt: { label: 'Gesamtkosten Unternehmer', istMonat: entnahmeIst + sozialIst + ekSteuerIst, vorjahrMonat: entnahmeVorjahr + sozialVorjahr + ekSteuerVorjahr }
        };
    }, [calc, unternehmerRawData, vUnternehmer, hasVorjahrData, vBwa, zeitraum]);

    // 3. Team Daten (Reine Werte-Übernahme aus dem Tresor)
    const teamDaten = useMemo(() => {
        const getVorjahr = (jsonId: string) => {
            if (!hasVorjahrData) return 0;
            if (Array.isArray(vPersonal)) {
                const jsonMatch = vPersonal.find((p: any) => p.id === jsonId);
                if (jsonMatch && jsonMatch.vorjahrMonat !== undefined) return parse(jsonMatch.vorjahrMonat);
            }
            return 0; 
        };

        const realProdGrund = (personalData || []).reduce((sum: number, p: any) => sum + parse(p.grundgehalt), 0);
        const realProdBoni = (personalData || []).reduce((sum: number, p: any) => sum + parse(p.bonus), 0);
        const realProdZusatz = (personalData || []).reduce((sum: number, p: any) => sum + parse(p.lohnzusatz || p.lohnkostenzusatz), 0);

        const realUnprodGrund = (unproductivePersonalData || []).reduce((sum: number, p: any) => sum + parse(p.grundgehalt), 0);
        const realUnprodZusatz = (unproductivePersonalData || []).reduce((sum: number, p: any) => sum + parse(p.lohnzusatz || p.lohnkostenzusatz), 0);

        const realAzubiGrund = (azubiAssistenzData || []).reduce((sum: number, p: any) => sum + parse(p.grundgehalt), 0);
        const realAzubiZusatz = (azubiAssistenzData || []).reduce((sum: number, p: any) => sum + parse(p.lohnzusatz || p.lohnkostenzusatz), 0);

        const hasRealProd = personalData && personalData.length > 0;
        const istProdGrund = hasRealProd ? realProdGrund : 0;
        const istProdBoni = hasRealProd ? realProdBoni : 0;
        const istProdZusatz = hasRealProd ? realProdZusatz : 0;

        const hasRealUnprod = unproductivePersonalData && unproductivePersonalData.length > 0;
        const istUnprodGrund = hasRealUnprod ? realUnprodGrund : 0;
        const istUnprodZusatz = hasRealUnprod ? realUnprodZusatz : 0;

        const hasRealAzubi = azubiAssistenzData && azubiAssistenzData.length > 0;
        const istAzubiGrund = hasRealAzubi ? realAzubiGrund : 0;
        const istAzubiZusatz = hasRealAzubi ? realAzubiZusatz : 0;

        return {
            produktiv: [
                { label: 'Grundlohn', istMonat: istProdGrund, vorjahrMonat: getVorjahr('p_grund') },
                { label: 'Boni / Provision', istMonat: istProdBoni, vorjahrMonat: getVorjahr('p_boni') },
                { label: 'Lohnzusatzkosten', istMonat: istProdZusatz, vorjahrMonat: getVorjahr('p_zusatz') },
            ],
            unproduktiv: [
                { label: 'Grundlohn', istMonat: istUnprodGrund, vorjahrMonat: getVorjahr('u_grund') },
                { label: 'Lohnzusatzkosten', istMonat: istUnprodZusatz, vorjahrMonat: getVorjahr('u_zusatz') },
            ],
            azubi: [
                { label: 'Grundlohn', istMonat: istAzubiGrund, vorjahrMonat: getVorjahr('a_grund') },
                { label: 'Lohnzusatzkosten', istMonat: istAzubiZusatz, vorjahrMonat: getVorjahr('a_zusatz') },
            ]
        };
    }, [personalData, unproductivePersonalData, azubiAssistenzData, vPersonal, hasVorjahrData]);

    const gesamtPersonalIst = useMemo(() => [...teamDaten.produktiv, ...teamDaten.unproduktiv, ...teamDaten.azubi].reduce((acc, curr) => acc + curr.istMonat, 0), [teamDaten]);
    const gesamtPersonalVorjahr = useMemo(() => [...teamDaten.produktiv, ...teamDaten.unproduktiv, ...teamDaten.azubi].reduce((acc, curr) => acc + curr.vorjahrMonat, 0), [teamDaten]);

    // 5. Kurzauswertung
    const dynamischeKurzauswertungVorjahr = useMemo(() => {
        if (hasVorjahrData && vCalcSnapshot) {
            return { ...calc, ...vCalcSnapshot }; 
        }
        return {
            ...calc,
            gemeinkostenfaktor: 0, materialkostenfaktor: 0, geraetekostenfaktor: 0, prodLohnkostenfaktor: 0,
            monatsumsatzNetto: 0, sollumsatz: 0, ueberschuss: 0, ueberschussProzent: 0, rentabilitaetSoll: 0
        };
    }, [calc, hasVorjahrData, vCalcSnapshot]);

    // 💉 CHIRURGIE: Exakte Gehaltswerte für Doppel-Schloss Extraktion (Aktuell & Vorjahr)
    const gehaltIst = unternehmerDaten.entnahme.istMonat + unternehmerDaten.sozial.istMonat;
    const gehaltVorjahr = unternehmerDaten.entnahme.vorjahrMonat + unternehmerDaten.sozial.vorjahrMonat;

    const totalAllInIstMonat = calc.sollumsatz || 0;
    const totalAllInVorjahrMonat = hasVorjahrData ? (dynamischeKurzauswertungVorjahr.sollumsatz || 0) : 0;
    const ueberschussIstMonat = calc.ueberschuss || 0;
    const ueberschussVorjahrMonat = hasVorjahrData ? (dynamischeKurzauswertungVorjahr.ueberschuss || 0) : 0;

    // 4. Daten für Kreisdiagramme
    // Material-€ wie Kostenstruktur (skaliert auf Umsatz); %-Labels = Anteil am Netto-Umsatz (nicht Donut-Summe)
    const { pieChartDataIst, pieChartDataVorjahr } = useMemo(() => {
        const personalGesamtIst = teamDaten.produktiv.reduce((a, b) => a + b.istMonat, 0) + 
                                  teamDaten.unproduktiv.reduce((a, b) => a + b.istMonat, 0) +
                                  teamDaten.azubi.reduce((a, b) => a + b.istMonat, 0);
        
        const raumIst = parse(d.raumkosten) / zeitraum;
        const matIst = Number(calc.materialkostenMonat) > 0
            ? Number(calc.materialkostenMonat)
            : (parse(d.material) / zeitraum);
        const werbeReiseIst = parse(d.werbeReisekosten) / zeitraum;
        const sonstigeIst = parse(d.sonstigeKosten) / zeitraum;
        const gewerbeIst = parse(d.gewerbesteuer) / zeitraum;
        const unternehmerIst = unternehmerDaten.gesamt.istMonat;
        // Rest = BWA-Summe abzgl. der ausgewiesenen Slices (Material bereits skaliert in Summe)
        const restIst = summeIstMonat - raumIst - matIst - werbeReiseIst - sonstigeIst - gewerbeIst;
        const umsatzIst = Number(calc.monatsumsatzNetto) || 0;

        const pieDataIst = [
            { name: 'Personalkosten', value: personalGesamtIst, color: '#3b82f6' }, 
            { name: 'Unternehmerkosten', value: unternehmerIst, color: '#eab308' }, 
            { name: 'Materialkosten', value: matIst, color: '#14b8a6' }, 
            { name: 'Raumkosten', value: raumIst, color: '#f59e0b' }, 
            { name: 'Werbung/Reisen', value: werbeReiseIst, color: '#ec4899' }, 
            { name: 'Sonstige Kosten', value: sonstigeIst, color: '#8b5cf6' }, 
            { name: 'Gewerbesteuer', value: gewerbeIst, color: '#ef4444' }, 
            { name: 'Übrige BWA', value: restIst > 0 ? restIst : 0, color: '#94a3b8' }, 
        ].filter(item => item.value > 0).map(item => ({
            ...item,
            percent: umsatzIst > 0 ? (item.value / umsatzIst) * 100 : 0,
        }));

        const personalGesamtVorjahr = teamDaten.produktiv.reduce((a, b) => a + b.vorjahrMonat, 0) + 
                                      teamDaten.unproduktiv.reduce((a, b) => a + b.vorjahrMonat, 0) +
                                      teamDaten.azubi.reduce((a, b) => a + b.vorjahrMonat, 0);
        
        const unternehmerVorjahr = unternehmerDaten.gesamt.vorjahrMonat;
        const raumVorjahr = hasVorjahrData && vBwa.raumkosten !== undefined ? (parse(vBwa.raumkosten) / zeitraum) : 0;
        const matVorjahrScaled = Number(dynamischeKurzauswertungVorjahr.materialkostenMonat) || 0;
        const matVorjahrRaw = hasVorjahrData && vBwa.material !== undefined ? (parse(vBwa.material) / zeitraum) : 0;
        const matVorjahr = matVorjahrScaled > 0 ? matVorjahrScaled : matVorjahrRaw;
        const werbeReiseVorjahr = hasVorjahrData && vBwa.werbeReisekosten !== undefined ? (parse(vBwa.werbeReisekosten) / zeitraum) : 0;
        const sonstigeVorjahr = hasVorjahrData && vBwa.sonstigeKosten !== undefined ? (parse(vBwa.sonstigeKosten) / zeitraum) : 0;
        const gewerbeVorjahr = hasVorjahrData && vBwa.gewerbesteuer !== undefined ? (parse(vBwa.gewerbesteuer) / zeitraum) : 0;
        const restVorjahr = summeVorjahrMonat - raumVorjahr - matVorjahr - werbeReiseVorjahr - sonstigeVorjahr - gewerbeVorjahr;
        const umsatzVorjahr = Number(dynamischeKurzauswertungVorjahr.monatsumsatzNetto) || 0;

        const pieDataVorjahr = [
            { name: 'Personalkosten', value: personalGesamtVorjahr, color: '#3b82f6' },
            { name: 'Unternehmerkosten', value: unternehmerVorjahr, color: '#eab308' },
            { name: 'Materialkosten', value: matVorjahr, color: '#14b8a6' },
            { name: 'Raumkosten', value: raumVorjahr, color: '#f59e0b' },
            { name: 'Werbung/Reisen', value: werbeReiseVorjahr, color: '#ec4899' },
            { name: 'Sonstige Kosten', value: sonstigeVorjahr, color: '#8b5cf6' },
            { name: 'Gewerbesteuer', value: gewerbeVorjahr, color: '#ef4444' },
            { name: 'Übrige BWA', value: restVorjahr > 0 ? restVorjahr : 0, color: '#94a3b8' },
        ].filter(item => item.value > 0).map(item => ({
            ...item,
            percent: umsatzVorjahr > 0 ? (item.value / umsatzVorjahr) * 100 : 0,
        }));

        return { pieChartDataIst: pieDataIst, pieChartDataVorjahr: pieDataVorjahr };
    }, [d, zeitraum, summeIstMonat, summeVorjahrMonat, teamDaten, unternehmerDaten, vBwa, hasVorjahrData, calc.materialkostenMonat, calc.monatsumsatzNetto, dynamischeKurzauswertungVorjahr]);

    // ==========================================
    // DATEI UPLOAD & RESET LOGIK FÜR JSON (REVISION D: MASTERDATEN)
    // ==========================================
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isGodMode) return; 
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                setImportError(null);
                setImportSuccess(false);
                const jsonString = event.target?.result as string;
                const parsed = JSON.parse(jsonString);
                
                const importBwaData = parsed.vorjahresBwaData || parsed.bwaData;
                const importUnternehmerData = parsed.vorjahresUnternehmerData || parsed.unternehmerData;
                const importPersonalData = parsed.vorjahresPersonalData || parsed.personalData;

                if (!importBwaData && !importUnternehmerData && !importPersonalData) {
                    throw new Error('Die Datei enthält keine gültigen Vorjahresdaten.');
                }

                const importGesamtleistungVorjahr = parsed.gesamtleistungVorjahr || [];
                const importUnternehmerStartData = parsed.unternehmerData || {};
                const importMitarbeiter = parsed.personalData || null;
                const importUnprod = parsed.unproductivePersonalData || null;
                const importAzubi = parsed.azubiAssistenzData || null;

                setClientData((prev: any) => {
                    let newGesamtleistungData = prev.gesamtleistungData || [];
                    if (importGesamtleistungVorjahr.length > 0) {
                        newGesamtleistungData = newGesamtleistungData.map((monat: any, index: number) => {
                            const vData = importGesamtleistungVorjahr[index];
                            if (vData) {
                                return {
                                    ...monat,
                                    vorjahrWert1: vData.vorjahrWert1 || 0,
                                    vorjahrWert2: vData.vorjahrWert2 || 0,
                                    vorjahrProzent: vData.vorjahrProzent || 0
                                };
                            }
                            return monat;
                        });
                    }

                    const newUnternehmerData = { ...prev.unternehmerData };
                    if (importUnternehmerStartData.entnahme !== undefined) newUnternehmerData.entnahme = importUnternehmerStartData.entnahme;
                    if (importUnternehmerStartData.sozialkosten !== undefined) newUnternehmerData.sozialkosten = importUnternehmerStartData.sozialkosten;
                    if (importUnternehmerStartData.einkommenssteuer !== undefined) newUnternehmerData.einkommenssteuer = importUnternehmerStartData.einkommenssteuer;
                    if (importUnternehmerStartData.prodWochenstunden !== undefined) newUnternehmerData.prodWochenstunden = importUnternehmerStartData.prodWochenstunden;
                    if (importUnternehmerStartData.prodWochentage !== undefined) newUnternehmerData.prodWochentage = importUnternehmerStartData.prodWochentage;
                    if (importUnternehmerStartData.geoeffneteStunden !== undefined) newUnternehmerData.geoeffneteStunden = importUnternehmerStartData.geoeffneteStunden;
                    if (importUnternehmerStartData.monatsUmsaetze !== undefined) newUnternehmerData.monatsUmsaetze = importUnternehmerStartData.monatsUmsaetze;
                    if (importUnternehmerStartData.durchschnittsumsatz !== undefined) {
                        newUnternehmerData.durchschnittsumsatz = importUnternehmerStartData.durchschnittsumsatz;
                        newUnternehmerData.durchschnittsumsatzVJ = importUnternehmerStartData.durchschnittsumsatzVJ || importUnternehmerStartData.durchschnittsumsatz;
                    }

                    return {
                        ...prev,
                        vorjahresBwaData: importBwaData ? { ...prev.vorjahresBwaData, ...importBwaData, isImported: true } : prev.vorjahresBwaData,
                        vorjahresUnternehmerData: importUnternehmerData ? { ...prev.vorjahresUnternehmerData, ...importUnternehmerData } : prev.vorjahresUnternehmerData,
                        vorjahresPersonalData: importPersonalData || prev.vorjahresPersonalData,
                        vorjahresCalcData: parsed.vorjahresCalcData || parsed.calcData || null,
                        gesamtleistungData: newGesamtleistungData,
                        unternehmerData: newUnternehmerData,
                        personalData: importMitarbeiter || prev.personalData,
                        unproductivePersonalData: importUnprod || prev.unproductivePersonalData,
                        azubiAssistenzData: importAzubi || prev.azubiAssistenzData
                    };
                });

                setImportSuccess(true);
                
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

            } catch (err: any) {
                setImportError('Fehler beim Auslesen der JSON-Datei: ' + err.message);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        };
        reader.readAsText(file);
    };

    const handleResetVorjahr = () => {
        if (!isGodMode) return; 
        if (window.confirm("Möchtest du alle historischen Vorjahresdaten für dieses Jahr löschen? Das Dashboard wird danach leer sein.")) {
            setClientData((prev: any) => ({
                ...prev,
                vorjahresBwaData: {},
                vorjahresUnternehmerData: {},
                vorjahresPersonalData: [],
                vorjahresCalcData: null
            }));
            setImportSuccess(false);
            setImportError(null);
        }
    };

    const umsatzIstNetto = Number(calc.monatsumsatzNetto) || 0;
    const umsatzVorjahrNetto = Number(dynamischeKurzauswertungVorjahr.monatsumsatzNetto) || 0;
    const pctVomUmsatz = (val: number, umsatz: number) => (umsatz > 0 ? (val / umsatz) * 100 : 0);

    const renderRow = (label: string, ist: number, vorjahr: number, key: string, isRevenue: boolean = false, isTotal: boolean = false) => (
        <div key={key} className={`grid grid-cols-[1fr_120px_110px_110px] md:grid-cols-[1fr_160px_120px_120px] gap-2 md:gap-4 items-center border-b border-pe-border dark:border-[#222] py-2.5 px-4 hover:bg-stone-200/40 dark:hover:bg-[#050505]/30 transition-colors text-sm ${isTotal ? 'bg-stone-200/50 dark:bg-[#050505]/50 font-bold' : ''}`}>
            <div className={`font-medium ${isTotal ? 'text-pe-accent-text' : isRevenue ? 'text-green-700 dark:text-green-500' : 'text-pe-muted dark:text-gray-300'}`}>{label}</div>
            
            {/* Differenz (Entwicklung) im Vordergrund */}
            <div className="text-right text-base md:text-lg">
                <DiffDisplay current={ist} prev={vorjahr} isRevenue={isRevenue} hasVorjahrData={hasVorjahrData} currencyCode={currencyCode} align="right" />
            </div>

            {/* Ist & Vorjahr: % vom Netto-Umsatz klein oben, Euro darunter */}
            <div className={`text-right font-mono text-xs md:text-sm ${isTotal ? 'text-pe-accent-text' : 'text-pe-muted dark:text-gray-400'}`}>
                <span className="md:hidden text-[9px] text-stone-500 dark:text-gray-500 mr-1 block">Ist:</span>
                <div className={`text-[9px] leading-none mb-0.5 font-mono ${isTotal ? 'text-[#8D6435]/70 dark:text-[#E6C299]/70' : 'text-teal-700 dark:text-teal-500/80'}`}>
                    {formatValue(pctVomUmsatz(ist, umsatzIstNetto), 1)}%
                </div>
                <div>{formatValue(ist)} {currencyCode}</div>
            </div>
            <div className="text-right font-mono text-xs md:text-sm text-stone-400 dark:text-gray-600">
                <span className="md:hidden text-[9px] text-stone-400 dark:text-gray-600 mr-1 block">VJ:</span>
                <div className="text-[9px] leading-none mb-0.5 font-mono text-stone-500 dark:text-gray-500">
                    {hasVorjahrData ? `${formatValue(pctVomUmsatz(vorjahr, umsatzVorjahrNetto), 1)}%` : '–'}
                </div>
                <div>{formatValue(vorjahr)} {currencyCode}</div>
            </div>
        </div>
    );

    if (!calc.ready) return <div className="bg-pe-bg dark:bg-[#030303] min-h-screen p-8 text-center text-pe-text">Lade Daten...</div>;

    return (
        <div className="bg-pe-bg dark:bg-[#030303] min-h-screen p-4 sm:p-6 lg:p-8 font-sans text-left">
            <CustomStyles />
            <div className="w-full max-w-[1400px] mx-auto space-y-8">
                
                <div className="flex items-center justify-between mb-8 text-left carbon-weave-bg border border-pe-border dark:border-[#2a2a2a] p-4 rounded-xl shadow-lg">
                    <div className="flex items-center gap-4">
                        <UI.PageTitle className="!mb-0 !border-0 !p-0 !rounded-none">VORJAHRES-VERGLEICH</UI.PageTitle>
                        <span className="bg-pe-tile-header dark:bg-[#1a1a1a] text-pe-accent-text border border-[#E6C299]/30 px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider">
                            Analyse Modus
                        </span>
                    </div>
                </div>

                {/* EBENE 1: BWA KOSTEN (MONAT) */}
                <Section className="text-left carbon-panel carbon-weave-bg border-none shadow-2xl">
                    <div className="carbon-content">
                        <SectionTitle>BWA Kosten (Monatliche Auswertung)</SectionTitle>
                        <p className="text-xs text-stone-500 dark:text-gray-500 mb-4 mt-1 px-4">Hinweis: Ungebuchte (leere) Konten werden ausgeblendet. Prozentwerte = Anteil am Netto-Umsatz (wie Kostenstruktur).</p>
                        
                        <div className="bg-pe-tile-header rounded-xl border border-pe-border dark:border-[#222] overflow-hidden mt-4 text-left shadow-inner">
                            
                            <div className="hidden md:grid grid-cols-[1fr_160px_120px_120px] gap-4 p-4 bg-pe-sunken border-b border-pe-border dark:border-[#222] font-bold text-xs uppercase tracking-wider text-pe-muted dark:text-gray-400 text-left">
                                <div>Kostenart</div>
                                <div className="text-right text-pe-accent-text">Differenz (+/-)</div>
                                <div className="text-right leading-tight">
                                    Aktuell
                                    <span className="block text-[9px] font-semibold text-teal-500/70 normal-case tracking-normal">% vom Umsatz</span>
                                </div>
                                <div className="text-right opacity-60 leading-tight">
                                    Vorjahr
                                    <span className="block text-[9px] font-semibold text-stone-500 dark:text-gray-500 normal-case tracking-normal">% vom Umsatz</span>
                                </div>
                            </div>

                            {materialDaten.length > 0 && (
                                <div className="border-b-2 border-pe-border dark:border-[#222]">
                                    <div className="px-4 py-2 text-[10px] uppercase font-bold text-teal-400 tracking-widest bg-teal-950/20">
                                        Materialeinsatz (Direkte Kosten)
                                    </div>
                                    {materialDaten.map((item, idx) => renderRow(item.label, item.istMonat, item.vorjahrMonat, `mat-${idx}`, item.isRevenue))}
                                </div>
                            )}

                            {betriebskostenDaten.length > 0 && (
                                <div className="border-b-2 border-pe-border dark:border-[#222]">
                                    <div className="px-4 py-2 text-[10px] uppercase font-bold text-pe-text tracking-widest bg-pe-tile-header dark:bg-[#1a1a1a]">
                                        Betriebskosten
                                    </div>
                                    {betriebskostenDaten.map((item, idx) => renderRow(item.label, item.istMonat, item.vorjahrMonat, `betr-${idx}`, item.isRevenue))}
                                </div>
                            )}

                            {geraeteDaten.length > 0 && (
                                <div className="border-b-2 border-pe-border dark:border-[#222]">
                                    <div className="px-4 py-2 text-[10px] uppercase font-bold text-pe-text tracking-widest bg-purple-100 dark:bg-purple-900/50">
                                        Kosmetische Geräte (Leasing/Kosten/Miete)
                                    </div>
                                    {geraeteDaten.map((item, idx) => renderRow(item.label, item.istMonat, item.vorjahrMonat, `ger-${idx}`, item.isRevenue))}
                                </div>
                            )}

                            {preisExpertDaten.length > 0 && (
                                <div className="border-b-2 border-pe-border dark:border-[#222]">
                                    <div className="px-4 py-2 text-[10px] uppercase font-bold text-pe-text tracking-widest bg-orange-100 dark:bg-orange-900/50">
                                        PreisExpert Beratung
                                    </div>
                                    {preisExpertDaten.map((item, idx) => renderRow(item.label, item.istMonat, item.vorjahrMonat, `px-${idx}`, item.isRevenue))}
                                </div>
                            )}

                            {positiveDaten.length > 0 && (
                                <div className="">
                                    <div className="px-4 py-2 text-[10px] uppercase font-bold text-pe-text tracking-widest bg-green-100 dark:bg-green-900/50 flex items-center gap-2">
                                        Positive Werte (Einnahmen / Abschreibungen)
                                    </div>
                                    {positiveDaten.map((item, idx) => renderRow(item.label, item.istMonat, item.vorjahrMonat, `pos-${idx}`, item.isRevenue))}
                                </div>
                            )}
                        </div>

                        {/* 💉 CHIRURGIE: EBENE 1A - GROSSE CARDS (Jetzt wieder unten in der BWA Section) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 p-4 text-left">
                            <div className="bg-pe-tile-header p-6 rounded-xl border-l-4 border-blue-500 shadow-lg flex justify-between items-center text-left border border-pe-border dark:border-[#222]">
                                <div>
                                    <div className="text-pe-muted dark:text-gray-400 text-[10px] uppercase font-bold tracking-widest mb-2 text-left">Kostenentwicklung pro Monat</div>
                                    <div className="text-3xl font-black font-mono text-left JetBrains Mono">
                                        <DiffDisplay current={summeIstMonat} prev={summeVorjahrMonat} isRevenue={false} hasVorjahrData={hasVorjahrData} currencyCode={currencyCode} align="left" />
                                    </div>
                                </div>
                                <div className="text-right flex flex-col gap-2 min-w-[150px]">
                                    <div className="bg-pe-tile-header dark:bg-[#1a1a1a] border border-pe-border-strong px-3 py-2 rounded flex flex-col items-end">
                                        <span className="text-stone-500 dark:text-gray-500 text-[9px] uppercase font-bold tracking-widest">Aktuell</span> 
                                        <span className="text-pe-text text-base font-black font-mono">{formatValue(summeIstMonat)} {currencyCode}</span>
                                    </div>
                                    <div className="bg-pe-sunken border border-pe-border dark:border-[#222] px-3 py-2 rounded flex flex-col items-end">
                                        <span className="text-stone-400 dark:text-gray-600 text-[9px] uppercase font-bold tracking-widest">Vorjahr</span> 
                                        <span className="text-pe-muted dark:text-gray-400 text-sm font-bold font-mono">{formatValue(summeVorjahrMonat)} {currencyCode}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-pe-tile-header p-6 rounded-xl border-l-4 border-[#E6C299] shadow-lg flex justify-between items-center text-left border border-pe-border dark:border-[#222]">
                                <div>
                                    <div className="text-pe-muted dark:text-gray-400 text-[10px] uppercase font-bold tracking-widest mb-2 text-left">Kostenentwicklung aktuelles Jahr</div>
                                    <div className="text-3xl font-black font-mono text-left JetBrains Mono">
                                        <DiffDisplay current={summeIstJahr} prev={summeVorjahrJahr} isRevenue={false} hasVorjahrData={hasVorjahrData} currencyCode={currencyCode} align="left" />
                                    </div>
                                </div>
                                <div className="text-right flex flex-col gap-2 min-w-[150px]">
                                    <div className="bg-pe-tile-header dark:bg-[#1a1a1a] border border-pe-border-strong px-3 py-2 rounded flex flex-col items-end">
                                        <span className="text-stone-500 dark:text-gray-500 text-[9px] uppercase font-bold tracking-widest">Aktuell</span> 
                                        <span className="text-pe-text text-base font-black font-mono">{formatValue(summeIstJahr)} {currencyCode}</span>
                                    </div>
                                    <div className="bg-pe-sunken border border-pe-border dark:border-[#222] px-3 py-2 rounded flex flex-col items-end">
                                        <span className="text-stone-400 dark:text-gray-600 text-[9px] uppercase font-bold tracking-widest">Vorjahr</span> 
                                        <span className="text-pe-muted dark:text-gray-400 text-sm font-bold font-mono">{formatValue(summeVorjahrJahr)} {currencyCode}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* EBENE 2: VISUELLE KOSTENVERTEILUNG (PIE CHARTS) */}
                <Section className="text-left carbon-panel carbon-weave-bg border-none shadow-2xl">
                    <div className="carbon-content">
                        <SectionTitle>Visuelle Kostenverteilung (Vergleich)</SectionTitle>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4 p-4">
                            
                            <div className="bg-pe-tile-header rounded-xl border border-pe-border dark:border-[#222] overflow-hidden flex flex-col shadow-lg">
                                <div className="p-4 bg-pe-sunken border-b border-pe-border dark:border-[#222] text-center">
                                    <span className="text-pe-accent-text font-bold uppercase tracking-widest text-sm">Aktuelles Jahr (Ist)</span>
                                </div>
                                <div className="p-4 h-[350px] w-full flex justify-center items-center">
                                    {pieChartDataIst.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart margin={{ top: 20, right: 90, bottom: 20, left: 90 }}>
                                                <Pie
                                                    data={pieChartDataIst}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={85}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                    stroke="none"
                                                    label={({ name, payload }) => `${name} (${Number(payload?.percent ?? 0).toFixed(1)}%)`}
                                                    labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                                                >
                                                    {pieChartDataIst.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip 
                                                    formatter={(value, _name, item) => {
                                                        const pct = Number(item?.payload?.percent ?? 0);
                                                        return `${formatValue(Number(value) || 0)} ${currencyCode} (${pct.toFixed(1)}% vom Umsatz)`;
                                                    }}
                                                    contentStyle={{ backgroundColor: '#111', borderRadius: '8px', border: '1px solid #222', color: '#fff' }}
                                                    itemStyle={{ color: '#E6C299' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <span className="text-stone-400 dark:text-gray-600 font-mono text-sm tracking-widest uppercase">Keine Daten vorhanden</span>
                                    )}
                                </div>
                            </div>

                            <div className="bg-pe-tile-header rounded-xl border border-pe-border dark:border-[#222] overflow-hidden flex flex-col shadow-lg opacity-90">
                                <div className="p-4 bg-pe-sunken border-b border-pe-border dark:border-[#222] text-center">
                                    <span className="text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest text-sm">Historie (Vorjahr)</span>
                                </div>
                                <div className="p-4 h-[350px] w-full flex justify-center items-center">
                                    {hasVorjahrData && pieChartDataVorjahr.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart margin={{ top: 20, right: 90, bottom: 20, left: 90 }}>
                                                <Pie
                                                    data={pieChartDataVorjahr}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={85}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                    stroke="none"
                                                    label={({ name, payload }) => `${name} (${Number(payload?.percent ?? 0).toFixed(1)}%)`}
                                                    labelLine={{ stroke: '#64748b', strokeWidth: 1 }}
                                                >
                                                    {pieChartDataVorjahr.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip 
                                                    formatter={(value, _name, item) => {
                                                        const pct = Number(item?.payload?.percent ?? 0);
                                                        return `${formatValue(Number(value) || 0)} ${currencyCode} (${pct.toFixed(1)}% vom Umsatz)`;
                                                    }}
                                                    contentStyle={{ backgroundColor: '#111', borderRadius: '8px', border: '1px solid #222', color: '#cbd5e1' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <span className="text-gray-700 font-mono text-sm tracking-widest uppercase">Keine Daten vorhanden</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* EBENE 3: UNTERNEHMER KOSTEN */}
                <Section className="text-left carbon-panel carbon-weave-bg border-none shadow-2xl">
                    <div className="carbon-content">
                        <SectionTitle>Unternehmer Kosten (Monatsauswertung)</SectionTitle>
                        <div className="bg-pe-tile-header rounded-xl border border-pe-border dark:border-[#222] overflow-hidden mt-4 text-left shadow-inner m-4">
                            <div className="bg-pe-sunken px-4 py-3 border-b border-pe-border dark:border-[#222] font-bold text-pe-text flex items-center gap-2 text-left">
                                <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                                Entnahmen, Sozialabgaben & EK-Steuer (Inhaber / Geschäftsführer)
                            </div>
                            {renderRow(unternehmerDaten.entnahme.label, unternehmerDaten.entnahme.istMonat, unternehmerDaten.entnahme.vorjahrMonat, 'u-ent')}
                            {renderRow(unternehmerDaten.sozial.label, unternehmerDaten.sozial.istMonat, unternehmerDaten.sozial.vorjahrMonat, 'u-soz')}
                            {renderRow(unternehmerDaten.ekSteuer.label, unternehmerDaten.ekSteuer.istMonat, unternehmerDaten.ekSteuer.vorjahrMonat, 'u-ek')}
                            
                            <div className="border-t border-pe-border dark:border-[#222] bg-stone-200/50 dark:bg-[#050505]/50 mt-1">
                                {renderRow(unternehmerDaten.gesamt.label, unternehmerDaten.gesamt.istMonat, unternehmerDaten.gesamt.vorjahrMonat, 'u-ges', false, true)}
                            </div>
                        </div>
                    </div>
                </Section>

                {/* EBENE 4: MITARBEITER KOSTEN (NUR MONAT) */}
                <Section className="text-left carbon-panel carbon-weave-bg border-none shadow-2xl">
                    <div className="carbon-content">
                        <SectionTitle>Mitarbeiter Kosten (Monatsauswertung)</SectionTitle>
                        <div className="space-y-6 mt-4 text-left p-4">
                            <div className="bg-pe-tile-header rounded-xl border border-pe-border dark:border-[#222] overflow-hidden text-left shadow-inner">
                                <div className="bg-pe-sunken px-4 py-3 border-b border-pe-border dark:border-[#222] font-bold text-pe-text flex items-center gap-2 text-left">
                                    <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                                    Produktive Mitarbeiter
                                </div>
                                {teamDaten.produktiv.map((item, idx) => renderRow(item.label, item.istMonat, item.vorjahrMonat, `prod-${idx}`))}
                            </div>

                            <div className="bg-pe-tile-header rounded-xl border border-pe-border dark:border-[#222] overflow-hidden text-left shadow-inner">
                                <div className="bg-pe-sunken px-4 py-3 border-b border-pe-border dark:border-[#222] font-bold text-pe-text flex items-center gap-2 text-left">
                                    <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                                    Unproduktive Mitarbeiter (Rezeption etc.)
                                </div>
                                {teamDaten.unproduktiv.map((item, idx) => renderRow(item.label, item.istMonat, item.vorjahrMonat, `unprod-${idx}`))}
                            </div>

                            <div className="bg-pe-tile-header rounded-xl border border-pe-border dark:border-[#222] overflow-hidden text-left shadow-inner">
                                <div className="bg-pe-sunken px-4 py-3 border-b border-pe-border dark:border-[#222] font-bold text-pe-text flex items-center gap-2 text-left">
                                    <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                                    Azubis / Assistenzen
                                </div>
                                {teamDaten.azubi.map((item, idx) => renderRow(item.label, item.istMonat, item.vorjahrMonat, `azubi-${idx}`))}
                            </div>

                            {/* SUMME PERSONAL */}
                            <div className="border-t border-pe-border dark:border-[#222] bg-stone-200/50 dark:bg-[#050505]/50 rounded-lg overflow-hidden shadow-inner p-1 mt-6">
                                {renderRow('Gesamtkosten Personal', gesamtPersonalIst, gesamtPersonalVorjahr, 'pers-ges', false, true)}
                            </div>
                        </div>
                    </div>
                </Section>

                {/* EBENE 5: KURZAUSWERTUNG (GESPIEGELT) */}
                <Section className="text-left carbon-panel carbon-weave-bg border-none shadow-2xl">
                    <div className="carbon-content">
                        <SectionTitle>Kalkulatorische Kurzauswertung (Vergleich)</SectionTitle>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mt-4 text-left p-4">
                            <KurzauswertungBlock 
                                calc={calc} 
                                title="Aktuelles Jahr (Ist-Werte)" 
                                hasData={true} 
                                currencyCode={currencyCode} 
                                gehalt={gehaltIst} 
                            />
                            <KurzauswertungBlock 
                                calc={dynamischeKurzauswertungVorjahr} 
                                title="Historie (Vorjahr)" 
                                isMock={true} 
                                hasData={hasVorjahrData && !!vCalcSnapshot} 
                                currencyCode={currencyCode} 
                                gehalt={gehaltVorjahr} 
                            />
                        </div>
                    </div>
                </Section>

                {/* EBENE 6: HOCHVERDICHTETER ZUSAMMENFASSUNGS-BLOCK */}
                <Section className="text-left mt-8 carbon-panel carbon-weave-bg border-none shadow-2xl">
                    <div className="carbon-content">
                        <SectionTitle>Betriebswirtschaftliche Zusammenfassung (Monat Ø)</SectionTitle>
                        <p className="text-xs text-stone-500 dark:text-gray-500 mb-4 mt-1 px-4">
                            Gegenüberstellung von Umsatz, Kosten und dem finalen kalkulatorischen Überschuss.
                        </p>

                        <div className="bg-pe-tile-header rounded-xl border border-pe-border dark:border-[#222] shadow-inner overflow-hidden m-4">
                            
                            {/* Tabellen-Kopf */}
                            <div className="hidden md:grid grid-cols-[1fr_200px_130px_130px] gap-4 p-4 bg-pe-sunken border-b border-pe-border dark:border-[#222] text-[10px] uppercase font-bold tracking-widest text-pe-muted dark:text-gray-400">
                                <div>Kennzahl</div>
                                <div className="text-right text-pe-accent-text">Entwicklung (+/-)</div>
                                <div className="text-right">Aktuell</div>
                                <div className="text-right opacity-70">Vorjahr</div>
                            </div>

                            {/* UMSATZ */}
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_130px_130px] gap-2 md:gap-4 items-center border-b border-stone-300/50 dark:border-[#222]/50 py-4 px-4 hover:bg-stone-200/50 dark:hover:bg-[#050505]/50 transition-colors">
                                <div className="font-bold text-pe-text text-sm">Gesamtumsatz (Netto)</div>
                                
                                <div className="text-right text-xl md:text-2xl font-black">
                                    <DiffDisplay current={calc.monatsumsatzNetto || 0} prev={dynamischeKurzauswertungVorjahr.monatsumsatzNetto || 0} isRevenue={true} hasVorjahrData={hasVorjahrData} currencyCode={currencyCode} align="right" />
                                </div>
                                
                                <div className="text-right font-mono font-bold text-pe-muted dark:text-gray-300 text-sm JetBrains Mono">
                                    <span className="md:hidden text-[10px] text-stone-500 dark:text-gray-500 uppercase mr-2">Aktuell:</span>
                                    {formatValue(calc.monatsumsatzNetto || 0)} {currencyCode}
                                </div>
                                <div className="text-right font-mono text-stone-400 dark:text-gray-600 text-sm JetBrains Mono">
                                    <span className="md:hidden text-[10px] text-stone-500 dark:text-gray-500 uppercase mr-2">Vorjahr:</span>
                                    {hasVorjahrData ? `${formatValue(dynamischeKurzauswertungVorjahr.monatsumsatzNetto || 0)} ${currencyCode}` : '-'}
                                </div>
                            </div>

                            {/* KOSTEN */}
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_130px_130px] gap-2 md:gap-4 items-center border-b border-stone-300/50 dark:border-[#222]/50 py-4 px-4 hover:bg-stone-200/50 dark:hover:bg-[#050505]/50 transition-colors">
                                <div className="font-bold text-pe-muted dark:text-gray-300 text-sm">Gesamtkosten All-In</div>
                                
                                <div className="text-right text-xl md:text-2xl font-black">
                                    <DiffDisplay current={totalAllInIstMonat} prev={totalAllInVorjahrMonat} isRevenue={false} hasVorjahrData={hasVorjahrData} currencyCode={currencyCode} align="right" />
                                </div>

                                <div className="text-right font-mono text-pe-muted dark:text-gray-400 text-sm JetBrains Mono">
                                    <span className="md:hidden text-[10px] text-stone-500 dark:text-gray-500 uppercase mr-2">Aktuell:</span>
                                    {formatValue(totalAllInIstMonat)} {currencyCode}
                                </div>
                                <div className="text-right font-mono text-stone-400 dark:text-gray-600 text-sm JetBrains Mono">
                                    <span className="md:hidden text-[10px] text-stone-500 dark:text-gray-500 uppercase mr-2">Vorjahr:</span>
                                    {hasVorjahrData ? `${formatValue(totalAllInVorjahrMonat)} ${currencyCode}` : '-'}
                                </div>
                            </div>

                            {/* ÜBERSCHUSS */}
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_130px_130px] gap-2 md:gap-4 items-center bg-stone-200/40 dark:bg-[#050505]/40 py-4 px-4">
                                <div className="font-black text-pe-accent-text text-sm uppercase tracking-wide">Überschuss / Gewinn</div>
                                
                                <div className="text-right text-xl md:text-2xl font-black">
                                    <DiffDisplay current={ueberschussIstMonat} prev={ueberschussVorjahrMonat} isRevenue={true} hasVorjahrData={hasVorjahrData} currencyCode={currencyCode} align="right" />
                                </div>
                                
                                <div className="text-right flex flex-col md:items-end">
                                    <span className="md:hidden text-[10px] text-stone-500 dark:text-gray-500 uppercase mr-2">Aktuell:</span>
                                    <span className="font-mono font-bold text-pe-text text-sm JetBrains Mono">{formatValue(ueberschussIstMonat)} {currencyCode}</span>
                                    <span className="text-[10px] text-stone-500 dark:text-gray-500">({formatValue(calc.ueberschussProzent || 0, 1)}%)</span>
                                </div>
                                
                                <div className="text-right flex flex-col md:items-end">
                                    <span className="md:hidden text-[10px] text-stone-500 dark:text-gray-500 uppercase mr-2">Vorjahr:</span>
                                    <span className="font-mono font-bold text-stone-400 dark:text-gray-600 text-sm JetBrains Mono">{hasVorjahrData ? `${formatValue(ueberschussVorjahrMonat)} ${currencyCode}` : '-'}</span>
                                    {hasVorjahrData && <span className="text-[10px] text-stone-400 dark:text-gray-600">({formatValue(dynamischeKurzauswertungVorjahr.ueberschussProzent || 0, 1)}%)</span>}
                                </div>
                            </div>

                        </div>
                    </div>
                </Section>

                {/* EBENE 7: ADMIN IMPORT ZONE (GOD MODE ONLY) */}
                {isGodMode && (
                    <Section className="mt-12 border border-red-500/50 bg-red-950/20 text-left carbon-panel border-none shadow-2xl">
                        <div className="carbon-content">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 p-6">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <ShieldAlert className="w-5 h-5 text-red-500" />
                                        <h3 className="text-lg font-bold text-pe-text uppercase tracking-widest">Admin / Coach Import-Zone</h3>
                                    </div>
                                    <p className="text-xs text-red-200/70 max-w-2xl leading-relaxed">
                                        Lade hier die vom Excel-Konverter generierte <span className="font-mono text-red-400">.json</span> Datei hoch, um die historischen Werte in dieses Geschäftsjahr fest zu importieren.
                                    </p>
                                </div>

                                <div className="flex flex-col md:flex-row items-center gap-4">
                                    <div className="flex flex-col items-center gap-2">
                                        <input
                                            type="file"
                                            accept=".json"
                                            ref={fileInputRef}
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            id="json-upload"
                                        />
                                        <label
                                            htmlFor="json-upload"
                                            className="cursor-pointer bg-red-600 text-white font-black text-xs uppercase tracking-widest px-6 py-3 rounded-lg hover:bg-red-500 transition-all shadow-lg flex items-center justify-center gap-2 border border-red-700 w-full md:w-auto"
                                        >
                                            <UploadCloud className="w-4 h-4"/> JSON Vorjahr hochladen
                                        </label>

                                        {importError && <span className="text-red-400 text-xs font-bold font-mono">{importError}</span>}
                                        {importSuccess && <span className="text-emerald-400 text-xs font-bold animate-pulse">✓ Daten gespeichert!</span>}
                                    </div>
                                    
                                    <button 
                                        onClick={handleResetVorjahr}
                                        className="bg-transparent text-pe-muted dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 font-bold text-xs uppercase tracking-widest px-4 py-3 rounded-lg transition-all flex items-center justify-center gap-2 border border-pe-border dark:border-[#222] hover:border-red-500 w-full md:w-auto"
                                    >
                                        <Trash2 className="w-4 h-4" /> Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Section>
                )}

            </div>
        </div>
    );
};

export default VorjahresKostenPage;